import React from 'react';
import css from './SectionWhyList.module.css';

const FeatureSection = ({ title, children }) => {
  return (
    <div className={css.featureSection}>
      <h3 className={css.featureTitle}>{title}</h3>
      <div className={css.featureContent}>{children}</div>
    </div>
  );
};

const SectionWhyList = () => {
  return (
    <div className={css.section}>
      <div className={css.backgroundOverlay} />
      <div className={css.blockContainer}>
        {/* Heading */}
        <div className={css.heading}>
          <h2 className={css.headingTitle}>
            Rennzone – The Ultimate Porsche Marketplace
          </h2>
          <p className={css.headingSubtitle}>
            Sell a Porsche. Buy a Porsche. Connect with enthusiasts worldwide.
          </p>
        </div>

        {/* Content */}
        <div>
          {/* Intro */}
          <div className={css.introSection}>
            <p className={css.introText}>
              Rennzone is the world's first dedicated Porsche marketplace where passion meets
              precision. Whether you're looking to sell your Porsche 911, find a Porsche Cayman
              for sale, or discover rare Porsche memorabilia, you've found your community. Built
              by Porsche enthusiasts for Porsche enthusiasts, we've created the ultimate platform
              where everything revolves around the marque we love.
            </p>
          </div>

          <FeatureSection title="Sell Your Porsche – Faster, Easier, Smarter">
            <p>
              Ready to sell your Porsche? You're in the right place. Rennzone makes it remarkably
              simple to connect with serious buyers who appreciate what makes your Porsche special.
            </p>
          </FeatureSection>

          <FeatureSection title="Why Sell Porsche Online with Rennzone?">
            <p>
              When you sell your Porsche 911, sell your Porsche Cayman, or sell your Porsche
              Boxster on Rennzone, you're not just posting another listing—you're reaching a
              global community of dedicated Porsche buyers. Our platform is designed exclusively
              for the marque, ensuring every interaction is relevant for the rare unique Porsche.
            </p>
            <p>
              List in minutes, not hours. Unlike traditional platforms, Rennzone's streamlined
              process gets your vehicle or parts online faster than anywhere else. Whether you're
              selling a pristine GT3, a project 997, or used Porsche parts, our intuitive system
              was built with sellers in mind.
            </p>
            <p>
              Competitive fees that respect your investment. We believe in fair pricing. That's
              why our listing fees are significantly lower than competitors, putting more money
              back in your pocket where it belongs.
            </p>
          </FeatureSection>

          <FeatureSection title="What Can You Sell?">
            <ul className={css.bulletList}>
              <li className={css.bulletItem}>
                <span className={css.bulletDot}>•</span>
                <span>
                  <strong className={css.bulletBold}>Cars:</strong> From classic air-cooled icons
                  to modern marvels, if it wears the Porsche crest, it belongs here
                </span>
              </li>
              <li className={css.bulletItem}>
                <span className={css.bulletDot}>•</span>
                <span>
                  <strong className={css.bulletBold}>Race Cars:</strong> Track-ready machines that
                  live for apex hunting
                </span>
              </li>
              <li className={css.bulletItem}>
                <span className={css.bulletDot}>•</span>
                <span>
                  <strong className={css.bulletBold}>Parts:</strong> Sell Porsche used parts online
                  to enthusiasts restoring their dreams
                </span>
              </li>
              <li className={css.bulletItem}>
                <span className={css.bulletDot}>•</span>
                <span>
                  <strong className={css.bulletBold}>Memorabilia:</strong> Sell Porsche memorabilia
                  online – from vintage posters to dealer literature
                </span>
              </li>
              <li className={css.bulletItem}>
                <span className={css.bulletDot}>•</span>
                <span>
                  <strong className={css.bulletBold}>Models:</strong> Sell Porsche models that
                  capture the spirit in miniature
                </span>
              </li>
              <li className={css.bulletItem}>
                <span className={css.bulletDot}>•</span>
                <span>
                  <strong className={css.bulletBold}>Premium Lifestyle:</strong> Watches, clothing,
                  books, and art celebrating the brand
                </span>
              </li>
            </ul>
          </FeatureSection>

          {/* Highlight Box */}
          <div className={css.highlightBox}>
            <p>
              Looking for the best place to sell your Porsche? Whether it's the best place to
              sell Porsche 911 or any Porsche-related item, Rennzone offers unmatched exposure to
              buyers who understand value and heritage.
            </p>
          </div>

          <FeatureSection title="Find Your Perfect Porsche">
            <p>
              Buy Porsche models from sellers who care as much as you do. Every listing on
              Rennzone comes from fellow enthusiasts and trusted dealers who share your passion.
            </p>
          </FeatureSection>

          <FeatureSection title="Discover Your Dream Machine">
            <p>
              Porsche 911 for sale listings span generations—from early long-hood classics to the
              latest 992. Browse Porsche Cayman for sale options perfect for backroad carving, or
              explore Porsche Boxster for sale convertibles that make every drive memorable.
            </p>
            <p>
              Seeking something special? Our Porsche GT3 for sale listings feature track-focused
              performance, while Porsche 997 for sale options offer the sweet spot of modern
              reliability and analog engagement. For UK-based buyers, explore Porsche UK used
              inventory from verified sellers nationwide.
            </p>
          </FeatureSection>

          <FeatureSection title="More Than Just Cars">
            <p>
              Used Porsche parts help keep your classic running, while rare memorabilia adds
              character to your garage or office. Whether you buy Porsche models for your
              collection or sourcing that impossible-to-find part, Rennzone connects you with
              sellers worldwide.
            </p>
          </FeatureSection>

          <FeatureSection title="Why Rennzone Is Different">
            <div className={css.subSections}>
              <div>
                <h3 className={css.subSectionTitle}>100% Porsche, 100% Focused</h3>
                <p>
                  This isn't another generic marketplace cluttered with everything under the sun.
                  Porsche marketplace is all we do, and we do it better than anyone else. When you
                  visit Rennzone, every listing, every seller, every conversation revolves around
                  the marque that started it all in Stuttgart.
                </p>
              </div>

              <div>
                <h3 className={css.subSectionTitle}>Built by Enthusiasts, For Enthusiasts</h3>
                <p>
                  We're not just a platform—we're Porsche people. We bleed Guards Red. Our
                  platform reflects the care and attention to detail that defines the brand itself.
                  From our streamlined listing process, to our global reach, every feature exists
                  to serve the Porsche community.
                </p>
              </div>

              <div>
                <h3 className={css.subSectionTitle}>Global Audience, Local Connection</h3>
                <p>
                  Whether you're in California, the UK, Germany, or anywhere between, Rennzone
                  brings together Porsche enthusiasts from every corner of the globe. Our
                  international reach means your listing gets seen by buyers who truly care, no
                  matter where they are.
                </p>
              </div>
            </div>
          </FeatureSection>

          <div className={css.separatorBlock}>
            <p>
              Sell used Porsche inventory to buyers across continents or find that perfect car
              from overseas. Our platform connects you with serious enthusiasts worldwide while
              maintaining the trust and transparency that defines the Porsche community.
            </p>
          </div>

          <FeatureSection title="Premium Experience, Fair Pricing">
            <p>
              We believe a premium marketplace doesn't need premium prices. Our competitive
              listing fees mean more value for sellers while maintaining the professional,
              dedicated platform buyers expect. Quality doesn't have to cost more—it just has to
              be done right.
            </p>
          </FeatureSection>

          <FeatureSection title="Join the Rennzone Community">
            <div className={css.subSections}>
              <div>
                <h3 className={css.subSectionTitle}>For Private Sellers</h3>
                <p>
                  Whether you're moving on from your beloved Porsche used daily driver or selling
                  parts from your restoration project, Rennzone gives you the tools and audience
                  you need. Our platform handles everything from classic air-cooled 911s to the
                  latest models, along with parts, memorabilia, and collectibles.
                </p>
              </div>

              <div>
                <h3 className={css.subSectionTitle}>For Trade Sellers</h3>
                <p>
                  Dealers and specialists, welcome home. Showcase your inventory to qualified
                  buyers actively searching for Porsche products. Our dedicated audience means
                  better leads, faster sales, and connections that matter.
                </p>
              </div>
            </div>
          </FeatureSection>

          <FeatureSection title="Quick to List, Easy to Sell">
            <p>
              Time is valuable. That's why we've eliminated the frustration of complex listing
              processes. Upload photos, add details, set your price, and reach thousands of
              potential buyers—all faster than anywhere else online.
            </p>
          </FeatureSection>

          <FeatureSection title="Start Your Journey Today">
            <p>
              Ready to sell Porsche online? Create your listing in minutes and tap into a global
              network of enthusiasts who understand what you're offering. Sell used Porsche
              inventory, rare parts, or cherished memorabilia to buyers who appreciate
              authenticity and heritage.
            </p>
            <p>
              Searching for your next Porsche? Browse our growing marketplace of verified listings
              from sellers who share your passion. From daily drivers to weekend warriors, from
              restoration parts to pristine collectibles, your next Porsche awaits.
            </p>
            <p>
              Rennzone is more than a marketplace—it's where the global Porsche community comes
              to connect, buy, and sell. Join us today and experience the difference a dedicated
              platform makes.
            </p>
            <p className={css.ctaText}>List now. The community is waiting.</p>
          </FeatureSection>
        </div>
      </div>
    </div>
  );
};

export default SectionWhyList;
